/**
 * Posts service - fetching and engagement actions for AT Protocol and ActivityPub posts
 */
import {
    Agent,
    AtpAgent,
    AppBskyFeedDefs,
    AppBskyEmbedImages,
    AppBskyEmbedExternal,
    AppBskyEmbedRecord,
    AppBskyEmbedRecordWithMedia,
    AppBskyEmbedVideo,
} from '@atproto/api'
import type { AppBskyActorDefs } from '@atproto/api'
import type { OAuthSession } from '@atproto/oauth-client-browser'
import { graphqlQuery } from '@/services/graphqlService'
import { isActivityPubHandle } from '@/lib/atExplore'
import { cleanBridgyFedHandle, cleanAPBridgedHandle } from '@/lib/bridge'
import {
    contentWarningFromATLabels,
    contentWarningFromSelfLabels,
    contentWarningFromActivityPub,
} from '@/lib/contentLabels'
import { fetchProfilesBatch } from '@/services/profileService'
import type { ProfileBasic } from '@/services/profileService'
import { fetchMastodonProfilePosts } from '@/services/mastodonService'
import { fetchPixelfedProfilePosts } from '@/services/pixelfedService'
import type { MastodonSession } from '@/lib/mastodon'
import { instanceUrlFromHandle } from '@/lib/pixelfed'
import type { PixelfedSession } from '@/lib/pixelfed'
import type {
    Post,
    PostsPage,
    PostEmbed,
    PostMetrics,
    PostAuthor,
    PostFacet,
    FeedEmbed,
    AuthorFeedFilter,
    GQLATPostsResponse,
    GQLATRepostsResponse,
    GQLAPPostsResponse,
    GQLAPPostResponse,
    GQLATPostRecord,
    GQLATRepostRecord,
    GQLAPPost,
    GQLRecordResponse,
    GQLSubjectReference,
} from '@/types/posts'

// --- GraphQL Queries ---

const AT_POSTS_QUERY = `
query GetUserPosts($identifier: String!, $limit: Int, $cursor: String) {
  user(identifier: $identifier) {
    records(collection: "app.bsky.feed.post", limit: $limit, cursor: $cursor) {
      records {
        uri
        cid
        rkey
        value
        author {
          did
          handle
          displayName
          avatar { cdn pds }
        }
        authorVerification { verified }
        embed {
          type
          images {
            alt
            urls { cdn pds }
            aspectRatio { width height }
          }
          external {
            uri
            title
            description
            thumb { cdn pds }
          }
          video {
            playlist
            thumbnail
            alt
            aspectRatio { width height }
          }
          record {
            record {
              uri cid rkey value
              author { did handle displayName avatar { cdn pds } }
              authorVerification { verified }
              embed {
                type
                images { alt urls { cdn pds } aspectRatio { width height } }
                external { uri title description thumb { cdn pds } }
              }
              backlinkSources { collection path count }
            }
            verification { verified }
          }
          media {
            type
            images { alt urls { cdn pds } aspectRatio { width height } }
            external { uri title description thumb { cdn pds } }
          }
        }
        backlinkSources { collection path count }
        subject {
          record {
            uri
            cid
            rkey
            value
            author {
              did
              handle
              displayName
              avatar { cdn pds }
            }
            authorVerification { verified }
            embed {
              type
              images {
                alt
                urls { cdn pds }
                aspectRatio { width height }
              }
              external {
                uri
                title
                description
                thumb { cdn pds }
              }
            }
            backlinkSources { collection path count }
          }
          verification { verified }
        }
      }
      cursor
    }
  }
}
`

const AT_REPOSTS_QUERY = `
query GetUserReposts($identifier: String!, $limit: Int, $cursor: String) {
  user(identifier: $identifier) {
    records(collection: "app.bsky.feed.repost", limit: $limit, cursor: $cursor) {
      records {
        uri
        rkey
        value
        author {
          did
          handle
          displayName
          avatar { cdn pds }
        }
        subject {
          record {
            uri
            cid
            rkey
            value
            author {
              did
              handle
              displayName
              avatar { cdn pds }
            }
            embed {
              type
              images {
                alt
                urls { cdn pds }
                aspectRatio { width height }
              }
              external {
                uri
                title
                description
                thumb { cdn pds }
              }
              media {
                type
                images { alt urls { cdn pds } aspectRatio { width height } }
                external { uri title description thumb { cdn pds } }
              }
            }
          }
        }
      }
      cursor
    }
  }
}
`

// NOTE: AP_POSTS_QUERY does not include the `quote` field because the current
// GraphQL schema does not support it (causes GRAPHQL_VALIDATION_FAILED).
// Quote posts are only rendered when the primary Mastodon direct-fetch path succeeds.
const AP_POSTS_QUERY = `
query GetAPUserPosts($handle: String!, $limit: Int, $cursor: String) {
  apUser(handle: $handle) {
    handle
    displayName
    avatarUrl
    verification { verified }
    posts(limit: $limit, cursor: $cursor) {
      posts {
        uri
        content
        createdAt
        activityType
        inReplyTo
        sensitive
        summary
        author { handle displayName avatarUrl }
        boost { boostedAt }
        images { url alt width height }
        externalEmbed { url title description thumbnailUrl }
        metrics { likeCount repostCount replyCount }
      }
      cursor
    }
  }
}
`

const RECORD_QUERY = `
query GetRecord($uri: String!) {
  record(uri: $uri) {
    uri
    cid
    value
    author {
      did
      handle
      displayName
      avatar { cdn pds }
    }
    authorVerification { verified }
    embed {
      type
      images {
        alt
        urls { cdn pds }
        aspectRatio { width height }
      }
      external {
        uri
        title
        description
        thumb { cdn pds }
      }
      record {
        record {
          uri cid rkey value
          author { did handle displayName avatar { cdn pds } }
          authorVerification { verified }
          embed {
            type
            images { alt urls { cdn pds } aspectRatio { width height } }
            external { uri title description thumb { cdn pds } }
          }
          backlinkSources { collection path count }
        }
        verification { verified }
      }
      media {
        type
        images { alt urls { cdn pds } aspectRatio { width height } }
        external { uri title description thumb { cdn pds } }
      }
    }
    backlinkSources { collection path count }
  }
}
`

const AP_POST_QUERY = `
query GetAPPost($uri: String!) {
  apPost(uri: $uri) {
    uri
    summary
    sensitive
    content
    createdAt
    author {
      handle
      displayName
      avatarUrl
    }
    metrics {
      likeCount
      repostCount
      replyCount
    }
  }
}
`

// --- Fetch Functions ---

export async function fetchPosts(
    identifier: string,
    options?: {
        limit?: number
        cursor?: string
        mastodonSession?: MastodonSession
        authorFeedFilter?: AuthorFeedFilter
        pixelfedSession?: PixelfedSession
    }
): Promise<PostsPage> {
    if (isActivityPubHandle(identifier)) {
        return fetchAPPosts(identifier, options)
    }
    return fetchATPosts(identifier, options)
}

function enrichAuthorFromProfile(
    author: PostAuthor | undefined,
    profileMap: Map<string, ProfileBasic>
): PostAuthor | undefined {
    if (!author) return undefined
    const fetched = profileMap.get(author.did)
    if (!fetched) return author
    return {
        ...author,
        displayName: author.displayName ?? fetched.displayName,
        avatarUrl: author.avatarUrl ?? fetched.avatar,
    }
}

/**
 * Fetch posts via Bluesky's getAuthorFeed API directly.
 * Always reads from the provided publicAgent (app view) so the user's app view
 * selection is respected. Viewer state (likes/reposts) is enriched separately
 * from the authenticated session when available.
 * Falls back to GraphQL/PDS collection query if the Bluesky API fails or returns no posts.
 */
export async function fetchAuthorFeed(
    identifier: string,
    options: {
        limit?: number
        cursor?: string
        filter?: AuthorFeedFilter
        session?: OAuthSession
        publicAgent: AtpAgent
        /**
         * Ask the AppView to prepend the actor's pinned post as a `reasonPin` feed item.
         * Page one only — the AppView ignores it when a cursor is supplied, and it removes
         * the chronological copy of the pinned post from that page.
         */
        includePins?: boolean
        /** Called when a fallback to PDS is triggered, so callers can notify the user */
        onFallback?: () => void
    }
): Promise<PostsPage> {
    const readAgent = options.publicAgent

    const filterMap: Record<AuthorFeedFilter, string> = {
        POSTS_NO_REPLIES: 'posts_no_replies',
        POSTS_WITH_REPLIES: 'posts_with_replies',
        POSTS_WITH_MEDIA: 'posts_with_media',
        POSTS_WITH_VIDEO: 'posts_with_video',
        POSTS_AND_AUTHOR_THREADS: 'posts_and_author_threads',
    }

    try {
        const response = await readAgent.app.bsky.feed.getAuthorFeed({
            actor: identifier,
            limit: options.limit ?? 25,
            cursor: options.cursor,
            filter: options.filter ? filterMap[options.filter] : undefined,
            ...(options.includePins && { includePins: true }),
        })

        let posts = response.data.feed.map(fvp => mapFeedViewPost(fvp))

        // Backfill missing displayName/avatar for authors with neither field set.
        // Bridged AP accounts often appear in getAuthorFeed with only a did and
        // bridge-domain handle. Batch-fetch their profiles to fill in the gaps.
        const missingDids = [
            ...new Set(
                posts
                    .flatMap(p => [p.author, p.repostedBy])
                    .filter((a): a is PostAuthor => a != null && !a.displayName && !a.avatarUrl)
                    .map(a => a.did)
            ),
        ]
        if (missingDids.length > 0) {
            try {
                const profileMap = await fetchProfilesBatch(missingDids, readAgent)
                posts = posts.map(post => ({
                    ...post,
                    author: enrichAuthorFromProfile(post.author, profileMap),
                    repostedBy: enrichAuthorFromProfile(post.repostedBy, profileMap),
                }))
            } catch (err) {
                console.warn('[authorFeed] profile backfill failed:', err)
            }
        }

        // If first page returned no posts, fall back to PDS via GraphQL
        if (posts.length === 0 && !options.cursor) {
            console.warn(
                '[authorFeed] Bluesky API returned empty, falling back to PDS via GraphQL' +
                    (options.filter ? ` (filter "${options.filter}" will not be applied)` : '')
            )
            options.onFallback?.()
            const fallbackResult = await fetchATPosts(identifier, {
                limit: options.limit,
                cursor: options.cursor,
            })
            return { ...fallbackResult, usedFallback: true }
        }

        // Enrich with viewer state from the authenticated session
        if (options.session) {
            const atUris = [
                ...new Set(
                    posts
                        .filter(p => !p.isActivityPub)
                        .flatMap(p => {
                            const uris = [p.uri]
                            if (p.parentPost && !p.parentPost.isActivityPub)
                                uris.push(p.parentPost.uri)
                            return uris
                        })
                ),
            ]
            if (atUris.length > 0) {
                try {
                    const viewerState = await fetchViewerState(options.session, atUris)
                    return {
                        posts: mergeViewerState(posts, viewerState),
                        cursor: response.data.cursor,
                    }
                } catch (err) {
                    console.warn('[viewer state] enrichment failed:', err)
                }
            }
        }

        return { posts, cursor: response.data.cursor }
    } catch (err) {
        console.warn(
            '[authorFeed] Bluesky API failed, falling back to PDS via GraphQL' +
                (options.filter ? ` (filter "${options.filter}" will not be applied)` : '') +
                ':',
            err
        )
        options.onFallback?.()
        const fallbackResult = await fetchATPosts(identifier, {
            limit: options.limit,
            cursor: options.cursor,
        })
        return { ...fallbackResult, usedFallback: true }
    }
}

/**
 * Fetch posts the actor has liked via Bluesky's getActorLikes API.
 * Returns FeedViewPost[] — same shape as getAuthorFeed — so we reuse mapFeedViewPost.
 * Requires authentication — the public API does not support this endpoint.
 */
export async function fetchActorLikes(
    actor: string,
    options: {
        limit?: number
        cursor?: string
        session: OAuthSession
        publicAgent?: AtpAgent
    }
): Promise<PostsPage> {
    // getActorLikes requires auth — use the authenticated agent
    const authedAgent = new Agent(options.session)

    const response = await authedAgent.app.bsky.feed.getActorLikes({
        actor,
        limit: options?.limit ?? 25,
        cursor: options?.cursor,
    })

    const posts = response.data.feed.map(fvp => mapFeedViewPost(fvp))

    // Enrich with viewer state from the authenticated session
    if (options?.session) {
        const atUris = [
            ...new Set(
                posts
                    .filter(p => !p.isActivityPub)
                    .flatMap(p => {
                        const uris = [p.uri]
                        if (p.parentPost && !p.parentPost.isActivityPub) uris.push(p.parentPost.uri)
                        return uris
                    })
            ),
        ]
        if (atUris.length > 0) {
            try {
                const viewerState = await fetchViewerState(options.session, atUris)
                return {
                    posts: mergeViewerState(posts, viewerState),
                    cursor: response.data.cursor,
                }
            } catch (err) {
                console.warn('[viewer state] enrichment failed:', err)
            }
        }
    }

    return { posts, cursor: response.data.cursor }
}

/**
 * Map a FeedViewPost from the Bluesky API to our normalized Post type
 */
export function mapFeedViewPost(fvp: AppBskyFeedDefs.FeedViewPost): Post {
    const postView = fvp.post
    const record = postView.record as Record<string, unknown>
    const text = (record.text as string) ?? ''
    const createdAt = (record.createdAt as string) ?? ''
    const reply = record.reply as
        | {
              parent?: { uri?: string }
              root?: { uri?: string; cid?: string }
          }
        | undefined

    // Extract rkey from URI (at://did/collection/rkey)
    const uriParts = postView.uri.split('/')
    const rkey = uriParts[uriParts.length - 1]

    // Detect reposts via reason
    const isRepost = fvp.reason?.$type === 'app.bsky.feed.defs#reasonRepost'
    const repostReason = isRepost ? (fvp.reason as AppBskyFeedDefs.ReasonRepost) : undefined

    // Detect the pinned-slot row the AppView prepends when includePins is requested
    const isPinnedEntry = fvp.reason?.$type === 'app.bsky.feed.defs#reasonPin'

    // Map author with verification
    const author = mapProfileViewBasicToAuthor(postView.author)

    // Map repost attribution
    const repostedBy = repostReason ? mapProfileViewBasicToAuthor(repostReason.by) : undefined

    // Map embeds — media, quoted post, and feed generator are handled separately
    const embed = mapPostViewEmbed(postView.embed)
    const quotedPost = mapQuotedPostFromView(postView.embed)
    const feedEmbed = mapFeedEmbedFromView(postView.embed)

    // Map inline parent post from hydrated reply ref (for replies tab context)
    const parentPost = mapParentPostFromReply(fvp.reply)

    // Detect deleted/blocked parent posts (NotFoundPost or BlockedPost)
    const replyParentNotFound =
        !!fvp.reply?.parent && fvp.reply.parent.$type !== 'app.bsky.feed.defs#postView'

    // Extract viewer state
    const likeUri = postView.viewer?.like ?? null
    const repostUri = postView.viewer?.repost ?? null
    const bookmarkUri = postView.viewer?.bookmarked ? postView.uri : null

    // Extract bridgyOriginalUrl from the raw record (Bridgy Fed bridged posts)
    const bridgyOriginalUrl =
        typeof record.bridgyOriginalUrl === 'string'
            ? record.bridgyOriginalUrl || undefined
            : undefined

    // Use repost indexedAt for feed ordering when it's a repost
    const displayCreatedAt = repostReason?.indexedAt ?? createdAt

    // For reposts of replies, suppress parent post rendering — only show the reposted reply itself
    const isReplyPost = !!fvp.reply || !!reply
    const showParent = isReplyPost && !isRepost

    return {
        uri: postView.uri,
        cid: postView.cid,
        rkey,
        text,
        facets: extractFacets(record),
        createdAt: displayCreatedAt,
        isReply: isReplyPost,
        replyParentUri: showParent
            ? (extractReplyParentUri(fvp.reply) ?? reply?.parent?.uri)
            : undefined,
        replyRootUri: extractReplyRootRef(fvp.reply)?.uri ?? reply?.root?.uri,
        replyRootCid: extractReplyRootRef(fvp.reply)?.cid ?? reply?.root?.cid,
        parentPost: showParent ? parentPost : undefined,
        replyParentNotFound: showParent ? replyParentNotFound || undefined : undefined,
        embed,
        quotedPost,
        feedEmbed,
        metrics: {
            likeCount: postView.likeCount ?? 0,
            repostCount: postView.repostCount ?? 0,
            replyCount: postView.replyCount ?? 0,
            quoteCount: postView.quoteCount,
        },
        author,
        repostedBy,
        likeUri,
        repostUri,
        bookmarkUri,
        isActivityPub: false,
        isRepost,
        ...(isPinnedEntry && { pinnedEntry: true, isPinned: true }),
        bridgyOriginalUrl,
        contentWarning: contentWarningFromATLabels(
            postView.labels as { val: string; neg?: boolean }[] | undefined
        ),
        ...(fvp.feedContext !== undefined && { feedContext: fvp.feedContext }),
        ...(fvp.reqId !== undefined && { reqId: fvp.reqId }),
    }
}

/**
 * Fetch a single AT Protocol post by URI using any Agent (public AtpAgent, or an
 * OAuth-session Agent — `AtpAgent extends Agent`, so both are accepted).
 * Returns a normalized Post or throws if not found.
 */
export async function fetchEmbeddedPost(uri: string, agent: Agent): Promise<Post> {
    const response = await agent.app.bsky.feed.getPosts({ uris: [uri] })
    const postView = response.data.posts[0]
    if (!postView) throw new Error('Post not found')
    return mapPostViewToPost(postView)
}

/**
 * Batch-fetch multiple AT Protocol posts by URI using the public app view agent.
 * Returns normalized Posts for all found URIs (missing/deleted posts are omitted).
 * Capped at 25 URIs (AppView limit).
 */
export async function fetchEmbeddedPosts(uris: string[], agent: AtpAgent): Promise<Post[]> {
    if (uris.length === 0) return []
    const capped = uris.slice(0, 25)
    const response = await agent.app.bsky.feed.getPosts({ uris: capped })
    return response.data.posts.map(mapPostViewToPost)
}

/**
 * Map a standalone PostView (e.g. from app.bsky.feed.getPosts) to our normalized Post type.
 * Similar to mapFeedViewPost but for PostView directly (no FeedViewPost wrapper).
 */
export function mapPostViewToPost(postView: AppBskyFeedDefs.PostView): Post {
    const record = postView.record as Record<string, unknown>
    const text = (record.text as string) ?? ''
    const createdAt = (record.createdAt as string) ?? ''
    const reply = record.reply as
        | { parent?: { uri?: string }; root?: { uri?: string; cid?: string } }
        | undefined

    const uriParts = postView.uri.split('/')
    const rkey = uriParts[uriParts.length - 1]

    const author = mapProfileViewBasicToAuthor(postView.author)
    const embed = mapPostViewEmbed(postView.embed)
    const quotedPost = mapQuotedPostFromView(postView.embed)
    const feedEmbed = mapFeedEmbedFromView(postView.embed)

    return {
        uri: postView.uri,
        cid: postView.cid,
        rkey,
        text,
        facets: extractFacets(record),
        createdAt,
        isReply: !!reply,
        replyParentUri: reply?.parent?.uri,
        replyRootUri: reply?.root?.uri,
        replyRootCid: reply?.root?.cid,
        embed,
        quotedPost,
        feedEmbed,
        metrics: {
            likeCount: postView.likeCount ?? 0,
            repostCount: postView.repostCount ?? 0,
            replyCount: postView.replyCount ?? 0,
            quoteCount: postView.quoteCount,
        },
        author,
        likeUri: postView.viewer?.like ?? null,
        repostUri: postView.viewer?.repost ?? null,
        bookmarkUri: null,
        isActivityPub: false,
        contentWarning: contentWarningFromATLabels(
            postView.labels as { val: string; neg?: boolean }[] | undefined
        ),
    }
}

/**
 * Map a ProfileViewBasic to our PostAuthor type
 */
export function mapProfileViewBasicToAuthor(
    profile: AppBskyActorDefs.ProfileViewBasic
): PostAuthor {
    return {
        did: profile.did,
        handle: profile.handle,
        displayName: profile.displayName,
        avatarUrl: profile.avatar,
        isVerified: profile.verification?.verifiedStatus === 'valid',
        // Extract follow status from viewer state when present (authenticated timeline responses).
        // null = not following (viewer present), undefined = no viewer state (unauthenticated).
        followingUri: profile.viewer !== undefined ? (profile.viewer.following ?? null) : undefined,
    }
}

/**
 * Extract facets from a raw AT Protocol record value.
 * Converts the raw facet format to our normalized PostFacet type.
 * Returns undefined if no link/mention/tag facets are present.
 */
export function extractFacets(record: Record<string, unknown>): PostFacet[] | undefined {
    const rawFacets = record.facets as
        | Array<{
              index?: { byteStart?: number; byteEnd?: number }
              features?: Array<{ $type?: string; uri?: string; did?: string; tag?: string }>
          }>
        | undefined
    if (!rawFacets?.length) return undefined

    const facets: PostFacet[] = []
    for (const facet of rawFacets) {
        if (!facet.index || facet.index.byteStart == null || facet.index.byteEnd == null) continue
        if (!facet.features?.length) continue

        const features: PostFacet['features'][number][] = []
        for (const f of facet.features) {
            if (f.$type === 'app.bsky.richtext.facet#link' && f.uri) {
                features.push({ type: 'link', uri: f.uri })
            } else if (f.$type === 'app.bsky.richtext.facet#mention' && f.did) {
                features.push({ type: 'mention', did: f.did })
            } else if (f.$type === 'app.bsky.richtext.facet#tag' && f.tag) {
                features.push({ type: 'tag', tag: f.tag })
            }
        }
        if (features.length > 0) {
            facets.push({
                index: { byteStart: facet.index.byteStart, byteEnd: facet.index.byteEnd },
                features,
            })
        }
    }

    return facets.length > 0 ? facets : undefined
}

/**
 * Extract parent URI from a hydrated ReplyRef (handles union type safely)
 */
function extractReplyParentUri(reply?: AppBskyFeedDefs.ReplyRef): string | undefined {
    if (!reply?.parent) return undefined
    if ('uri' in reply.parent) return reply.parent.uri
    return undefined
}

/**
 * Extract root strongRef (uri + cid) from a hydrated ReplyRef.
 * More reliable than the raw record body because the appview always hydrates the root PostView.
 */
function extractReplyRootRef(
    reply?: AppBskyFeedDefs.ReplyRef
): { uri: string; cid: string } | undefined {
    if (!reply?.root) return undefined
    if ('uri' in reply.root && 'cid' in reply.root) {
        return { uri: reply.root.uri, cid: reply.root.cid }
    }
    return undefined
}

/**
 * Map the parent post from a hydrated ReplyRef to an inline Post
 * Only maps when the parent is a PostView (not NotFoundPost/BlockedPost)
 * Also extracts grandparentAuthor to show "Replying to @author" context
 */
function mapParentPostFromReply(reply?: AppBskyFeedDefs.ReplyRef): Post | undefined {
    if (!reply?.parent) return undefined
    if (reply.parent.$type !== 'app.bsky.feed.defs#postView') return undefined

    const parentView = reply.parent as AppBskyFeedDefs.PostView
    const record = parentView.record as Record<string, unknown>
    const text = (record.text as string) ?? ''
    const createdAt = (record.createdAt as string) ?? ''
    const uriParts = parentView.uri.split('/')
    const rkey = uriParts[uriParts.length - 1]

    // Check if the parent post is itself a reply
    const parentReply = record.reply as
        | { parent?: { uri?: string }; root?: { uri?: string; cid?: string } }
        | undefined
    const parentIsReply = !!parentReply

    // Extract grandparent author (the person the parent was replying to)
    const grandparentAuthor = reply.grandparentAuthor
        ? {
              ...mapProfileViewBasicToAuthor(reply.grandparentAuthor),
              displayHandle:
                  cleanBridgyFedHandle(reply.grandparentAuthor.handle) ??
                  cleanAPBridgedHandle(reply.grandparentAuthor.handle) ??
                  undefined,
          }
        : undefined

    // Extract bridgyOriginalUrl from the raw record (Bridgy Fed bridged posts)
    const bridgyOriginalUrl =
        typeof record.bridgyOriginalUrl === 'string'
            ? record.bridgyOriginalUrl || undefined
            : undefined

    return {
        uri: parentView.uri,
        cid: parentView.cid,
        rkey,
        text,
        facets: extractFacets(record),
        createdAt,
        isReply: parentIsReply,
        replyParentUri: parentReply?.parent?.uri,
        replyParentAuthor: grandparentAuthor,
        embed: mapPostViewEmbed(parentView.embed),
        quotedPost: mapQuotedPostFromView(parentView.embed),
        feedEmbed: mapFeedEmbedFromView(parentView.embed),
        metrics: {
            likeCount: parentView.likeCount ?? 0,
            repostCount: parentView.repostCount ?? 0,
            replyCount: parentView.replyCount ?? 0,
            quoteCount: parentView.quoteCount,
        },
        author: mapProfileViewBasicToAuthor(parentView.author),
        likeUri: parentView.viewer?.like ?? null,
        repostUri: parentView.viewer?.repost ?? null,
        isActivityPub: false,
        isRepost: false,
        bridgyOriginalUrl,
        contentWarning: contentWarningFromATLabels(
            parentView.labels as { val: string; neg?: boolean }[] | undefined
        ),
    }
}

/**
 * Map hydrated embed view types from PostView to our PostEmbed
 * Handles images, external links, video, and recordWithMedia (media portion only)
 */
export function mapPostViewEmbed(embed: AppBskyFeedDefs.PostView['embed']): PostEmbed | undefined {
    if (!embed) return undefined

    const embedType = embed.$type

    // Images
    if (embedType === 'app.bsky.embed.images#view') {
        const imagesView = embed as AppBskyEmbedImages.View
        return {
            type: 'images',
            images: imagesView.images.map(img => ({
                url: img.thumb,
                fullsize: img.fullsize,
                alt: img.alt || undefined,
                aspectRatio: img.aspectRatio,
            })),
        }
    }

    // External link
    if (embedType === 'app.bsky.embed.external#view') {
        const externalView = embed as AppBskyEmbedExternal.View
        // AppView attaches `associatedRefs` and enrichment fields on the inner `external` object.
        const inner = externalView.external as AppBskyEmbedExternal.ViewExternal & {
            associatedRefs?: Array<{ uri: string }>
            createdAt?: string
            readingTime?: number
            source?: {
                title?: string
                icon?: string
                uri?: string
                theme?: {
                    backgroundRGB?: string
                    foregroundRGB?: string
                    accentRGB?: string
                    accentForegroundRGB?: string
                }
            }
            associatedProfiles?: Array<{
                did: string
                handle: string
                displayName?: string
                avatar?: string
            }>
        }
        return {
            type: 'external',
            external: {
                uri: inner.uri,
                title: inner.title || undefined,
                description: inner.description || undefined,
                thumb: inner.thumb,
                ...(inner.createdAt ? { createdAt: inner.createdAt } : {}),
                ...(inner.readingTime !== undefined ? { readingTime: inner.readingTime } : {}),
                ...(inner.source ? { source: inner.source } : {}),
                ...(inner.associatedProfiles?.length
                    ? {
                          associatedProfiles: inner.associatedProfiles.map(p => ({
                              did: p.did,
                              handle: p.handle,
                              displayName: p.displayName,
                              avatar: p.avatar,
                          })),
                      }
                    : {}),
            },
            ...(inner.associatedRefs && inner.associatedRefs.length > 0
                ? { associatedRefs: inner.associatedRefs.map(r => ({ uri: r.uri })) }
                : {}),
        }
    }

    // Video
    if (embedType === 'app.bsky.embed.video#view') {
        const videoView = embed as AppBskyEmbedVideo.View
        return {
            type: 'video',
            video: {
                playlist: videoView.playlist,
                thumbnail: videoView.thumbnail,
                aspectRatio: videoView.aspectRatio,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty alt text is meaningfully "no alt text", same as missing
                alt: videoView.alt || undefined,
            },
        }
    }

    // recordWithMedia — extract just the media portion (quote post handled separately)
    if (embedType === 'app.bsky.embed.recordWithMedia#view') {
        const rwmView = embed as AppBskyEmbedRecordWithMedia.View
        return mapPostViewEmbed(rwmView.media as AppBskyFeedDefs.PostView['embed'])
    }

    // Record-only embed (pure quote post) — no media embed to show
    return undefined
}

/**
 * Extract a quoted post from a PostView's embed, if present
 * Handles both record#view (pure quote) and recordWithMedia#view
 */
export function mapQuotedPostFromView(embed: AppBskyFeedDefs.PostView['embed']): Post | undefined {
    if (!embed) return undefined

    let recordView: AppBskyEmbedRecord.View | undefined

    if (embed.$type === 'app.bsky.embed.record#view') {
        recordView = embed as AppBskyEmbedRecord.View
    } else if (embed.$type === 'app.bsky.embed.recordWithMedia#view') {
        const rwmView = embed as AppBskyEmbedRecordWithMedia.View
        recordView = rwmView.record
    }

    if (!recordView) return undefined

    // Only handle ViewRecord (actual post content)
    const viewRecord = recordView.record
    if (viewRecord.$type !== 'app.bsky.embed.record#viewRecord') return undefined

    const qr = viewRecord as AppBskyEmbedRecord.ViewRecord
    const value = qr.value as Record<string, unknown>
    const text = (value.text as string) ?? ''
    const createdAt = (value.createdAt as string) ?? ''

    // Extract rkey from URI
    const uriParts = qr.uri.split('/')
    const rkey = uriParts[uriParts.length - 1]

    // Map nested embeds from the quoted post (first embed only)
    const nestedEmbed = qr.embeds?.length
        ? mapPostViewEmbed(qr.embeds[0] as AppBskyFeedDefs.PostView['embed'])
        : undefined

    return {
        uri: qr.uri,
        cid: qr.cid,
        rkey,
        text,
        facets: extractFacets(value),
        createdAt,
        isReply: false,
        embed: nestedEmbed,
        metrics: {
            likeCount: qr.likeCount ?? 0,
            repostCount: qr.repostCount ?? 0,
            replyCount: qr.replyCount ?? 0,
            quoteCount: qr.quoteCount,
        },
        author: mapProfileViewBasicToAuthor(qr.author),
        likeUri: null,
        repostUri: null,
        isActivityPub: false,
        isRepost: false,
        contentWarning: contentWarningFromATLabels(
            qr.labels as { val: string; neg?: boolean }[] | undefined
        ),
    }
}

/**
 * Extract a feed generator embed from a PostView's embed, if present.
 * Feed generators appear as app.bsky.feed.defs#generatorView inside record embeds.
 */
export function mapFeedEmbedFromView(
    embed: AppBskyFeedDefs.PostView['embed']
): FeedEmbed | undefined {
    if (!embed) return undefined

    let recordView: AppBskyEmbedRecord.View | undefined

    if (embed.$type === 'app.bsky.embed.record#view') {
        recordView = embed as AppBskyEmbedRecord.View
    } else if (embed.$type === 'app.bsky.embed.recordWithMedia#view') {
        const rwmView = embed as AppBskyEmbedRecordWithMedia.View
        recordView = rwmView.record
    }

    if (!recordView) return undefined

    const record = recordView.record
    if (record.$type !== 'app.bsky.feed.defs#generatorView') return undefined

    const gen = record as AppBskyFeedDefs.GeneratorView
    return {
        uri: gen.uri,
        displayName: gen.displayName,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty description is the same as none for display purposes
        description: gen.description || undefined,
        avatar: gen.avatar,
        likeCount: gen.likeCount,
        creator: {
            did: gen.creator.did,
            handle: gen.creator.handle,
            displayName: gen.creator.displayName,
            avatarUrl: gen.creator.avatar,
        },
    }
}

/**
 * Extract a feed generator embed from GraphQL ATPost embed data, if present.
 * Feed generators appear as embedded records whose URI contains app.bsky.feed.generator.
 */
export function mapFeedEmbedFromRecord(embed?: GQLATPostRecord['embed']): FeedEmbed | undefined {
    if (!embed) return undefined

    // The record portion is in embed.record for both plain record and recordWithMedia embeds
    const embedRecord = embed.record
    if (!embedRecord?.record) return undefined

    const rec = embedRecord.record

    // Detect feed generators by collection in the AT URI
    if (!rec.uri.includes('/app.bsky.feed.generator/')) return undefined

    const value = rec.value as Record<string, unknown>
    const displayName = (value.displayName as string) ?? ''
    if (!displayName) return undefined

    return {
        uri: rec.uri,
        displayName,
        description: typeof value.description === 'string' ? value.description : undefined,
        // Avatar is a blob ref in the GraphQL path and cannot be resolved to a URL here
        avatar: undefined,
        creator: {
            did: rec.author?.did ?? '',
            handle: rec.author?.handle ?? '',
            displayName: rec.author?.displayName,
            avatarUrl: rec.author?.avatar?.cdn ?? rec.author?.avatar?.pds,
        },
    }
}

async function fetchATPosts(
    identifier: string,
    options?: { limit?: number; cursor?: string }
): Promise<PostsPage> {
    const response = await graphqlQuery<GQLATPostsResponse>(AT_POSTS_QUERY, {
        identifier,
        limit: options?.limit ?? 25,
        cursor: options?.cursor,
    })

    if (response.errors?.length) {
        throw new Error(response.errors[0].message)
    }

    const records = response.data?.user?.records
    if (!records) {
        return { posts: [], cursor: undefined }
    }

    return {
        posts: records.records.map(mapATPostRecord),
        cursor: records.cursor ?? undefined,
    }
}

/**
 * Fetch AP profile posts, trying the instance API first
 * and falling back to GraphQL when the direct fetch fails.
 *
 * When a mastodonSession is available, fetches through the user's own instance
 * so that engagement state (favourited/reblogged) is returned and federated
 * profiles (e.g. bsky.brid.gy bridged users) are correctly resolved.
 *
 * Routes to fetchPixelfedProfilePosts when the pixelfedSession's instance
 * matches the profile's instance, so Pixelfed profiles are always fetched
 * with authentication even when a mastodonSession is also present.
 */
async function fetchAPPosts(
    handle: string,
    options?: {
        limit?: number
        cursor?: string
        mastodonSession?: MastodonSession
        authorFeedFilter?: AuthorFeedFilter
        pixelfedSession?: PixelfedSession
    }
): Promise<PostsPage> {
    const onlyMedia =
        options?.authorFeedFilter === 'POSTS_WITH_MEDIA' ||
        options?.authorFeedFilter === 'POSTS_WITH_VIDEO'
    try {
        // Route to the correct fetcher based on which session matches the profile's instance
        const profileOrigin = new URL(instanceUrlFromHandle(handle)).origin

        if (options?.pixelfedSession) {
            try {
                const pixelfedOrigin = new URL(options.pixelfedSession.instance_url).origin
                if (pixelfedOrigin === profileOrigin) {
                    // onlyMedia is not forwarded: Pixelfed profiles only expose Posts
                    // and Likes tabs (no Media tab), so authorFeedFilter is never
                    // POSTS_WITH_MEDIA for this code path.
                    return await fetchPixelfedProfilePosts(handle, {
                        cursor: options.cursor,
                        limit: options.limit,
                        pixelfedSession: options.pixelfedSession,
                    })
                }
            } catch {
                /* URL parse failure, fall through */
            }
        }

        // When a Mastodon session is available, fetch through the user's own instance.
        // Mastodon instances have federated data for remote accounts (including bridged
        // users like bsky.brid.gy) and return engagement state (favourited/reblogged)
        // because we're making an authenticated request.
        const instanceOverride = options?.mastodonSession?.instance_url

        return await fetchMastodonProfilePosts(handle, {
            cursor: options?.cursor,
            limit: options?.limit,
            mastodonSession: options?.mastodonSession,
            onlyMedia,
            instanceOverride,
            // Pins lead page one of the Posts tab only (decision: Posts-tab-only, both protocols)
            includePinned: !options?.cursor && options?.authorFeedFilter === 'POSTS_NO_REPLIES',
        })
    } catch (err) {
        if (onlyMedia) {
            console.warn(
                '[fetchAPPosts] Mastodon direct fetch failed for media filter — GraphQL fallback does not support media-only filtering'
            )
        }
        console.warn('[fetchAPPosts] Mastodon direct fetch failed, falling back to GraphQL:', err)
        return fetchAPPostsViaGraphQL(handle, options)
    }
}

async function fetchAPPostsViaGraphQL(
    handle: string,
    options?: { limit?: number; cursor?: string }
): Promise<PostsPage> {
    const response = await graphqlQuery<GQLAPPostsResponse>(AP_POSTS_QUERY, {
        handle,
        limit: options?.limit ?? 25,
        cursor: options?.cursor,
    })

    if (response.errors?.length) {
        throw new Error(response.errors[0].message)
    }

    const apUser = response.data?.apUser
    if (!apUser?.posts) {
        return { posts: [], cursor: undefined }
    }

    const author = {
        did: '', // AP users don't have DIDs
        handle: apUser.handle,
        displayHandle: cleanBridgyFedHandle(apUser.handle) ?? apUser.handle,
        displayName: apUser.displayName,
        avatarUrl: apUser.avatarUrl,
        isVerified: apUser.verification?.verified ?? false,
    }

    return {
        posts: apUser.posts.posts.map(post => mapAPPost(post, author)),
        cursor: apUser.posts.cursor ?? undefined,
    }
}

export async function fetchPostByUri(uri: string): Promise<Post | null> {
    const response = await graphqlQuery<GQLRecordResponse>(RECORD_QUERY, { uri })

    if (response.errors?.length) {
        throw new Error(response.errors[0].message)
    }

    const record = response.data?.record
    if (!record) return null

    const value = record.value as Record<string, unknown>
    const text = (value.text as string) ?? ''
    const createdAt = (value.createdAt as string) ?? ''
    const reply = value.reply as
        | {
              parent?: { uri?: string }
              root?: { uri?: string; cid?: string }
          }
        | undefined

    // Extract quoted post from embed.record if present
    const quotedPost = record.embed?.record ? mapQuotedPost(record.embed.record) : undefined

    return {
        uri: record.uri,
        cid: record.cid,
        text,
        facets: extractFacets(value),
        createdAt,
        isReply: !!reply,
        replyParentUri: reply?.parent?.uri,
        replyRootUri: reply?.root?.uri,
        replyRootCid: reply?.root?.cid,
        quotedPost,
        embed: mapATEmbed(record.embed),
        metrics: extractATMetrics(record.backlinkSources),
        author: record.author
            ? {
                  did: record.author.did,
                  handle: record.author.handle,
                  displayName: record.author.displayName,
                  avatarUrl: record.author.avatar?.cdn ?? record.author.avatar?.pds,
                  isVerified: record.authorVerification?.verified ?? false,
              }
            : undefined,
        likeUri: null,
        repostUri: null,
        isActivityPub: false,
        contentWarning: contentWarningFromSelfLabels(value),
    }
}

/**
 * Resolve a repost URI to the original post it references.
 * Fetches the repost record to extract the subject post URI,
 * then fetches the original post. Returns null if either step fails.
 */
export async function fetchRepostSubjectPost(repostUri: string): Promise<Post | null> {
    // Fetch the repost record to get the original post URI
    const repostResponse = await graphqlQuery<GQLRecordResponse>(RECORD_QUERY, { uri: repostUri })
    const repostRecord = repostResponse.data?.record
    if (!repostRecord) return null

    const value = repostRecord.value as Record<string, unknown>
    const subject = value.subject as { uri?: string; cid?: string } | undefined
    if (!subject?.uri) return null

    // Fetch the original post
    return fetchPostByUri(subject.uri)
}

/**
 * Fetch reposts for an AT Protocol user.
 * Uses the app.bsky.feed.repost collection via GraphQL, which resolves
 * subject records server-side — no separate getPosts batch call needed.
 */
export async function fetchATReposts(
    identifier: string,
    options?: { limit?: number; cursor?: string }
): Promise<PostsPage> {
    try {
        const response = await graphqlQuery<GQLATRepostsResponse>(AT_REPOSTS_QUERY, {
            identifier,
            limit: options?.limit ?? 25,
            cursor: options?.cursor,
        })

        if (response.errors?.length) {
            const msg = response.errors[0].message
            console.error('[fetchATReposts] GraphQL error:', msg, { identifier })
            throw new Error(`Failed to load reposts: ${msg}`)
        }

        const records = response.data?.user?.records
        if (!records) {
            return { posts: [], cursor: undefined }
        }

        // Filter out reposts where subject couldn't be resolved
        const validReposts = records.records.filter(r => r.subject?.record)

        return {
            posts: validReposts.map(mapATRepostRecord),
            cursor: records.cursor ?? undefined,
        }
    } catch (err) {
        if (err instanceof Error && err.message.startsWith('Failed to load reposts:')) {
            throw err
        }
        console.error('[fetchATReposts] Unexpected error:', err, { identifier })
        throw new Error('Failed to load reposts. Please try again.')
    }
}

/**
 * Map a repost record to a Post with isRepost flag
 */
export function mapATRepostRecord(record: GQLATRepostRecord): Post {
    const subjectRecord = record.subject!.record!
    const subjectValue = subjectRecord.value as Record<string, unknown>
    const text = (subjectValue.text as string) ?? ''
    const createdAt = (subjectValue.createdAt as string) ?? ''
    const reply = subjectValue.reply as
        | {
              parent?: { uri?: string }
              root?: { uri?: string; cid?: string }
          }
        | undefined

    // The repost author (who reposted)
    const repostedBy: PostAuthor | undefined = record.author
        ? {
              did: record.author.did,
              handle: record.author.handle,
              displayName: record.author.displayName,
              avatarUrl: record.author.avatar?.cdn ?? record.author.avatar?.pds,
              isVerified: record.authorVerification?.verified ?? false,
          }
        : undefined

    // The original post author
    const author: PostAuthor | undefined = subjectRecord.author
        ? {
              did: subjectRecord.author.did,
              handle: subjectRecord.author.handle,
              displayName: subjectRecord.author.displayName,
              avatarUrl: subjectRecord.author.avatar?.cdn ?? subjectRecord.author.avatar?.pds,
              isVerified:
                  record.subject?.verification?.verified ??
                  subjectRecord.authorVerification?.verified ??
                  false,
          }
        : undefined

    // Use the repost's createdAt for sorting (when the repost happened)
    const repostCreatedAt = (record.value as { createdAt?: string })?.createdAt ?? createdAt

    // Extract quoted post from embed.record if present
    const quotedPost = subjectRecord.embed?.record
        ? mapQuotedPost(subjectRecord.embed.record)
        : undefined

    return {
        uri: subjectRecord.uri,
        cid: subjectRecord.cid,
        rkey: subjectRecord.rkey,
        text,
        facets: extractFacets(subjectValue),
        createdAt: repostCreatedAt, // Use repost time for feed ordering
        isReply: !!reply,
        replyParentUri: reply?.parent?.uri,
        replyRootUri: reply?.root?.uri,
        replyRootCid: reply?.root?.cid,
        embed: mapATEmbed(subjectRecord.embed),
        quotedPost,
        metrics: extractATMetrics(subjectRecord.backlinkSources),
        author,
        likeUri: null,
        repostUri: null,
        isActivityPub: false,
        isRepost: true,
        repostedBy,
        repostRecordUri: record.uri,
        contentWarning: contentWarningFromSelfLabels(subjectValue),
    }
}

/**
 * Fetch a single ActivityPub post by URI
 */
export async function fetchAPPost(uri: string): Promise<Post | null> {
    const response = await graphqlQuery<GQLAPPostResponse>(AP_POST_QUERY, { uri })

    if (response.errors?.length) {
        throw new Error(response.errors[0].message)
    }

    const apPost = response.data?.apPost
    if (!apPost) return null

    const text = stripHTML(apPost.content)

    return {
        uri: apPost.uri,
        cid: undefined,
        text,
        createdAt: apPost.createdAt ?? '',
        isReply: false,
        embed: undefined, // AP single post doesn't include embeds in this query yet
        metrics: {
            likeCount: apPost.metrics?.likeCount ?? 0,
            repostCount: apPost.metrics?.repostCount ?? 0,
            replyCount: apPost.metrics?.replyCount ?? 0,
        },
        author: apPost.author
            ? {
                  did: '',
                  handle: apPost.author.handle,
                  displayHandle: cleanBridgyFedHandle(apPost.author.handle) ?? apPost.author.handle,
                  displayName: apPost.author.displayName,
                  avatarUrl: apPost.author.avatarUrl,
                  isVerified: false,
              }
            : undefined,
        likeUri: null,
        repostUri: null,
        isActivityPub: true,
        contentWarning: contentWarningFromActivityPub(apPost.sensitive, apPost.summary),
    }
}

// --- Mapping Functions (exported for testing) ---

/**
 * Map a quoted post from embed.record data
 */
export function mapQuotedPost(
    embedRecord: NonNullable<GQLATPostRecord['embed']>['record']
): Post | undefined {
    if (!embedRecord?.record) return undefined

    const qr = embedRecord.record

    // Feed generators and other non-post records are not quoted posts
    if (!qr.uri.includes('/app.bsky.feed.post/')) return undefined

    const value = qr.value as Record<string, unknown>
    const text = (value.text as string) ?? ''
    const createdAt = (value.createdAt as string) ?? ''

    return {
        uri: qr.uri,
        cid: qr.cid,
        rkey: qr.rkey,
        text,
        facets: extractFacets(value),
        createdAt,
        isReply: false,
        embed: mapATEmbed(qr.embed),
        metrics: extractATMetrics(qr.backlinkSources),
        author: qr.author
            ? {
                  did: qr.author.did,
                  handle: qr.author.handle,
                  displayName: qr.author.displayName,
                  avatarUrl: qr.author.avatar?.cdn ?? qr.author.avatar?.pds,
                  isVerified:
                      embedRecord.verification?.verified ??
                      qr.authorVerification?.verified ??
                      false,
              }
            : undefined,
        likeUri: null,
        repostUri: null,
        isActivityPub: false,
        contentWarning: contentWarningFromSelfLabels(value),
    }
}

/**
 * Map inline parent post from GraphQL subject reference
 */
function mapInlineParentPost(subject: GQLSubjectReference): Post | undefined {
    if (!subject.record) return undefined

    const parentRecord = subject.record
    const parentValue = parentRecord.value as Record<string, unknown>
    const parentText = (parentValue.text as string) ?? ''
    const parentCreatedAt = (parentValue.createdAt as string) ?? ''
    const parentReply = parentValue.reply as
        | {
              parent?: { uri?: string }
              root?: { uri?: string; cid?: string }
          }
        | undefined

    // Extract bridgyOriginalUrl from the raw record (Bridgy Fed bridged posts)
    const bridgyOriginalUrl =
        typeof parentValue.bridgyOriginalUrl === 'string'
            ? parentValue.bridgyOriginalUrl || undefined
            : undefined

    return {
        uri: parentRecord.uri,
        cid: parentRecord.cid,
        rkey: parentRecord.rkey,
        text: parentText,
        facets: extractFacets(parentValue),
        createdAt: parentCreatedAt,
        isReply: !!parentReply,
        replyParentUri: parentReply?.parent?.uri,
        replyRootUri: parentReply?.root?.uri,
        replyRootCid: parentReply?.root?.cid,
        embed: mapATEmbed(parentRecord.embed),
        metrics: extractATMetrics(parentRecord.backlinkSources),
        bridgyOriginalUrl,
        author: parentRecord.author
            ? {
                  did: parentRecord.author.did,
                  handle: parentRecord.author.handle,
                  displayName: parentRecord.author.displayName,
                  avatarUrl: parentRecord.author.avatar?.cdn ?? parentRecord.author.avatar?.pds,
                  isVerified:
                      subject.verification?.verified ??
                      parentRecord.authorVerification?.verified ??
                      false,
              }
            : undefined,
        likeUri: null,
        repostUri: null,
        isActivityPub: false,
        contentWarning: contentWarningFromSelfLabels(parentValue),
    }
}

export function mapATPostRecord(record: GQLATPostRecord): Post {
    const value = record.value as Record<string, unknown>
    const text = (value.text as string) ?? ''
    const createdAt = (value.createdAt as string) ?? ''
    const reply = value.reply as
        | {
              parent?: { uri?: string }
              root?: { uri?: string; cid?: string }
          }
        | undefined
    const parentUri = reply?.parent?.uri

    // Map inline parent post from subject if available
    const parentPost = record.subject ? mapInlineParentPost(record.subject) : undefined

    // Extract quoted post from embed.record if present
    const quotedPost = record.embed?.record ? mapQuotedPost(record.embed.record) : undefined

    // Extract feed generator embed from embed.record if present
    const feedEmbed = mapFeedEmbedFromRecord(record.embed)

    // Extract bridgyOriginalUrl from the raw record (Bridgy Fed bridged posts)
    const bridgyOriginalUrl =
        typeof value.bridgyOriginalUrl === 'string'
            ? value.bridgyOriginalUrl || undefined
            : undefined

    return {
        uri: record.uri,
        cid: record.cid,
        rkey: record.rkey,
        text,
        facets: extractFacets(value),
        createdAt,
        isReply: !!reply,
        replyParentUri: parentUri,
        replyRootUri: reply?.root?.uri,
        replyRootCid: reply?.root?.cid,
        parentPost,
        quotedPost,
        feedEmbed,
        embed: mapATEmbed(record.embed),
        metrics: extractATMetrics(record.backlinkSources),
        author: record.author
            ? {
                  did: record.author.did,
                  handle: record.author.handle,
                  displayName: record.author.displayName,
                  avatarUrl: record.author.avatar?.cdn ?? record.author.avatar?.pds,
                  isVerified: record.authorVerification?.verified ?? false,
              }
            : undefined,
        likeUri: null,
        repostUri: null,
        isActivityPub: false,
        bridgyOriginalUrl,
        contentWarning: contentWarningFromSelfLabels(value),
    }
}

export function mapAPPost(
    apPost: GQLAPPost,
    author?: {
        did: string
        handle: string
        displayHandle?: string
        displayName?: string
        avatarUrl?: string
        isVerified?: boolean
    }
): Post {
    // Strip quote-inline fallback text when the quote will be rendered as an embed
    const hasAcceptedQuote = apPost.quote?.state === 'accepted' && !!apPost.quote.quotedPost
    const htmlContent = hasAcceptedQuote ? stripQuoteInline(apPost.content) : apPost.content
    const text = stripHTML(htmlContent)

    // For boosts (Announce activity), the profile owner is the booster.
    // The per-post author field contains the original post's author.
    const isBoost = apPost.activityType === 'Announce' && !!apPost.boost
    const resolvedAuthor =
        isBoost && apPost.author
            ? {
                  did: '',
                  handle: apPost.author.handle,
                  displayHandle: cleanBridgyFedHandle(apPost.author.handle) ?? apPost.author.handle,
                  displayName: apPost.author.displayName,
                  avatarUrl: apPost.author.avatarUrl,
                  isVerified: false,
              }
            : author
    const boostedBy =
        isBoost && author
            ? {
                  handle: author.handle,
                  displayHandle: author.displayHandle ?? author.handle,
                  displayName: author.displayName,
                  avatarUrl: author.avatarUrl,
              }
            : undefined

    // Extract quoted post from GraphQL quote data (Mastodon 4.4+)
    const quotedPost = mapAPQuotedPost(apPost.quote)

    return {
        uri: apPost.uri,
        cid: undefined,
        text,
        createdAt: isBoost ? apPost.boost!.boostedAt : apPost.createdAt,
        isReply: !!apPost.inReplyTo,
        embed: mapAPEmbed(apPost),
        quotedPost,
        metrics: {
            likeCount: apPost.metrics?.likeCount ?? 0,
            repostCount: apPost.metrics?.repostCount ?? 0,
            replyCount: apPost.metrics?.replyCount ?? 0,
        },
        author: resolvedAuthor,
        boostedBy,
        likeUri: null,
        repostUri: null,
        isActivityPub: true,
        contentWarning: contentWarningFromActivityPub(apPost.sensitive, apPost.summary),
    }
}

export function extractATMetrics(
    backlinkSources?: { collection: string; path: string; count: number }[]
): PostMetrics {
    if (!backlinkSources) return { likeCount: 0, repostCount: 0, replyCount: 0 }

    return {
        likeCount: backlinkSources.find(s => s.collection === 'app.bsky.feed.like')?.count ?? 0,
        replyCount:
            backlinkSources.find(
                s => s.collection === 'app.bsky.feed.post' && s.path === 'reply.parent.uri'
            )?.count ?? 0,
        repostCount: backlinkSources.find(s => s.collection === 'app.bsky.feed.repost')?.count ?? 0,
    }
}

// --- Embed Mapping ---

export function mapATEmbed(embed?: GQLATPostRecord['embed']): PostEmbed | undefined {
    if (!embed) return undefined

    // For recordWithMedia, extract just the media portion as the embed
    // (the quoted post is handled separately via Post.quotedPost)
    if (embed.type === 'app.bsky.embed.recordWithMedia#view' && embed.media) {
        return mapATEmbed(embed.media)
    }

    // For record-only embeds (pure quote post), no media embed to show
    if (embed.type === 'app.bsky.embed.record#view') {
        return undefined
    }

    if (embed.images?.length) {
        return {
            type: 'images',
            images: embed.images.map(img => ({
                url: img.urls?.cdn ?? img.urls?.pds ?? '',
                alt: img.alt,
                aspectRatio: img.aspectRatio,
            })),
        }
    }

    if (embed.external) {
        return {
            type: 'external',
            external: {
                uri: embed.external.uri,
                title: embed.external.title,
                description: embed.external.description,
                thumb: embed.external.thumb?.cdn ?? embed.external.thumb?.pds,
            },
        }
    }

    if (embed.video) {
        return {
            type: 'video',
            video: {
                playlist: embed.video.playlist,
                thumbnail: embed.video.thumbnail,
                aspectRatio: embed.video.aspectRatio,
                alt: embed.video.alt,
            },
        }
    }

    return undefined
}

function mapAPEmbed(apPost: Pick<GQLAPPost, 'images' | 'externalEmbed'>): PostEmbed | undefined {
    if (apPost.images?.length) {
        return {
            type: 'images',
            images: apPost.images.map(img => ({
                url: img.url,
                alt: img.alt,
                aspectRatio:
                    img.width && img.height ? { width: img.width, height: img.height } : undefined,
            })),
        }
    }

    if (apPost.externalEmbed) {
        return {
            type: 'external',
            external: {
                uri: apPost.externalEmbed.url,
                title: apPost.externalEmbed.title,
                description: apPost.externalEmbed.description,
                thumb: apPost.externalEmbed.thumbnailUrl,
            },
        }
    }

    return undefined
}

/**
 * Map a GraphQL AP quoted post to a normalized Post, if the quote is accepted.
 * Returns undefined for non-accepted quotes or missing data.
 */
function mapAPQuotedPost(quote: GQLAPPost['quote']): Post | undefined {
    if (!quote || quote.state !== 'accepted' || !quote.quotedPost) return undefined

    const qp = quote.quotedPost
    const qpText = stripHTML(qp.content)

    return {
        uri: qp.uri,
        cid: undefined,
        text: qpText,
        createdAt: qp.createdAt,
        isReply: false,
        embed: mapAPEmbed(qp),
        metrics: { likeCount: 0, repostCount: 0, replyCount: 0 },
        author: qp.author
            ? {
                  did: '',
                  handle: qp.author.handle,
                  displayName: qp.author.displayName,
                  avatarUrl: qp.author.avatarUrl,
              }
            : undefined,
        likeUri: null,
        repostUri: null,
        isActivityPub: true,
    }
}

// --- HTML Stripping ---

/**
 * Remove elements with the `quote-inline` CSS class from HTML content.
 * Mastodon uses these as fallback text for the quoted post, which is
 * redundant when we display the quoted post embed separately.
 */
export function stripQuoteInline(html: string): string {
    // Mastodon may use <span> or <p> for quote-inline elements, so match any tag
    return html.replace(/<(\w+)[^>]+class="[^"]*\bquote-inline\b[^"]*"[^>]*>[\s\S]*?<\/\1>/gi, '')
}

export function stripHTML(html: string): string {
    let result = html

    // Convert block elements to newlines
    result = result.replace(/<\/p>/gi, '\n\n')
    result = result.replace(/<p[^>]*>/gi, '')
    result = result.replace(/<br\s*\/?>/gi, '\n')

    // Remove remaining HTML tags
    result = result.replace(/<[^>]+>/g, '')

    // Decode HTML entities
    result = result
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')

    // Clean up excessive newlines
    result = result.replace(/\n{3,}/g, '\n\n')

    return result.trim()
}

// --- Viewer State Enrichment ---

/** Per-post viewer state returned by fetchViewerState */
export type ViewerStateEntry = {
    likeUri: string | null
    repostUri: string | null
    /** Following URI for the post's author (string = following, null = not following, undefined = no viewer data) */
    authorFollowingUri?: string | null
}

/**
 * Fetch viewer engagement state (like/repost URIs) for a batch of AT Protocol post URIs.
 * Uses the authenticated user's session to call getPosts, which returns viewer state.
 * Also collects authorFollowingUri so callers can skip per-post getProfile calls.
 * Batches requests in groups of 25 (API limit).
 */
export async function fetchViewerState(
    session: OAuthSession,
    uris: string[]
): Promise<Map<string, ViewerStateEntry>> {
    if (uris.length === 0) return new Map()
    const agent = new Agent(session)

    // getPosts accepts max 25 URIs per call — batch in parallel
    const batches: string[][] = []
    for (let i = 0; i < uris.length; i += 25) {
        batches.push(uris.slice(i, i + 25))
    }

    const responses = await Promise.all(
        batches.map(batch => agent.app.bsky.feed.getPosts({ uris: batch }))
    )

    const map = new Map<string, ViewerStateEntry>()
    for (const response of responses) {
        for (const postView of response.data.posts) {
            map.set(postView.uri, {
                likeUri: postView.viewer?.like ?? null,
                repostUri: postView.viewer?.repost ?? null,
                authorFollowingUri:
                    postView.author.viewer !== undefined
                        ? (postView.author.viewer.following ?? null)
                        : undefined,
            })
        }
    }
    return map
}

/**
 * Merge viewer engagement state into an array of posts.
 * Updates likeUri, repostUri, and author.followingUri from the enriched viewer state.
 */
export function mergeViewerState(
    posts: Post[],
    viewerState: Map<string, ViewerStateEntry>
): Post[] {
    return posts.map(post => {
        const state = viewerState.get(post.uri)
        const updatedAuthor =
            state?.authorFollowingUri !== undefined && post.author
                ? { ...post.author, followingUri: state.authorFollowingUri }
                : post.author
        const updated = state
            ? { ...post, likeUri: state.likeUri, repostUri: state.repostUri, author: updatedAuthor }
            : post

        // Also enrich inline parent post
        if (updated.parentPost) {
            const parentState = viewerState.get(updated.parentPost.uri)
            if (parentState) {
                const updatedParentAuthor =
                    parentState.authorFollowingUri !== undefined && updated.parentPost.author
                        ? {
                              ...updated.parentPost.author,
                              followingUri: parentState.authorFollowingUri,
                          }
                        : updated.parentPost.author
                return {
                    ...updated,
                    parentPost: {
                        ...updated.parentPost,
                        likeUri: parentState.likeUri,
                        repostUri: parentState.repostUri,
                        author: updatedParentAuthor,
                    },
                }
            }
        }
        return updated
    })
}

// --- Timeline ---

/**
 * Fetch the authenticated user's home timeline (following feed).
 *
 * The caller supplies a pre-built agent (from AppViewContext's sessionAgent).
 * When a non-default AppView is selected, that agent already carries the
 * atproto-proxy header so the PDS forwards via service auth. No agent
 * construction or .withProxy call happens here — proxy wiring is centralised
 * in useAppView().
 */
export async function fetchTimeline(
    agent: Agent,
    options?: { limit?: number; cursor?: string }
): Promise<PostsPage> {
    const response = await agent.app.bsky.feed.getTimeline({
        limit: options?.limit ?? 20,
        cursor: options?.cursor,
    })
    const posts = response.data.feed.map(fvp => mapFeedViewPost(fvp))
    return { posts, cursor: response.data.cursor }
}

// --- Engagement Actions ---

export async function likePost(
    session: OAuthSession,
    postUri: string,
    postCid: string
): Promise<string> {
    const agent = new Agent(session)
    const response = await agent.like(postUri, postCid)
    return response.uri
}

export async function unlikePost(session: OAuthSession, likeUri: string): Promise<void> {
    const agent = new Agent(session)
    await agent.deleteLike(likeUri)
}

export async function repostPost(
    session: OAuthSession,
    postUri: string,
    postCid: string
): Promise<string> {
    const agent = new Agent(session)
    const response = await agent.repost(postUri, postCid)
    return response.uri
}

export async function unrepostPost(session: OAuthSession, repostUri: string): Promise<void> {
    const agent = new Agent(session)
    await agent.deleteRepost(repostUri)
}

export async function deletePost(session: OAuthSession, postUri: string): Promise<void> {
    const agent = new Agent(session)
    await agent.deletePost(postUri)
}

// --- Bookmark Actions ---

/**
 * Bookmark a post on Bluesky.
 * Returns the post URI (used as bookmarkUri sentinel).
 */
export async function bookmarkBlueskyPost(
    session: OAuthSession,
    postUri: string,
    postCid: string
): Promise<string> {
    const agent = new Agent(session)
    await agent.app.bsky.bookmark.createBookmark({ uri: postUri, cid: postCid })
    return postUri
}

/**
 * Remove a Bluesky bookmark.
 * @param bookmarkUri - the post URI (used as the bookmark identifier)
 */
export async function unbookmarkBlueskyPost(session: OAuthSession, postUri: string): Promise<void> {
    const agent = new Agent(session)
    await agent.app.bsky.bookmark.deleteBookmark({ uri: postUri })
}

/**
 * Fetch the authenticated user's Bluesky bookmarks.
 *
 * Returns a PostsPage with normalized Post entries.
 * Filters out blocked/not-found items from the response.
 */
export async function fetchBlueskyBookmarks(
    session: OAuthSession,
    options?: { limit?: number; cursor?: string }
): Promise<PostsPage> {
    const agent = new Agent(session)
    const response = await agent.app.bsky.bookmark.getBookmarks({
        limit: options?.limit ?? 25,
        cursor: options?.cursor,
    })

    const posts: Post[] = []
    for (const bookmarkView of response.data.bookmarks) {
        // Skip blocked or not-found posts
        if (bookmarkView.item.$type !== 'app.bsky.feed.defs#postView') continue
        const postView = bookmarkView.item as AppBskyFeedDefs.PostView
        // Wrap in a minimal FeedViewPost to reuse the existing mapper
        const fvp = { post: postView } as AppBskyFeedDefs.FeedViewPost
        const post = mapFeedViewPost(fvp)
        // Always mark as bookmarked since it came from the bookmark feed
        posts.push({ ...post, bookmarkUri: post.uri })
    }

    return { posts, cursor: response.data.cursor }
}
